// frontend/src/pages/asset-admin/AssetAdminCategories.js
/**
 * Asset Admin Categories Management
 * 
 * Manage asset categories and types following the design system.
 * Theme: Red (Admin)
 * 
 * @see frontend/src/styles/design-system-reference.md
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/UI/Modal';
import {
  FolderOpen, Package, Plus, Edit2, ChevronDown, ChevronRight,
  Laptop, Car, FileKey, Briefcase,
  CheckCircle, XCircle, Layers, X, DoorOpen, CreditCard, Fuel, Smartphone,
  Store, ShieldCheck, ShieldAlert, Loader2, Wrench, Cog,
  MapPin, Phone, Mail, User, Trash2, UserPlus,
} from 'lucide-react';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { InfoBadge } from '../../components/UI/InfoBadge';
import FormSelect from '../../components/UI/FormSelect';
import { FilterButton } from '../../components/UI/FilterButton';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import DatalistCombobox from '../../components/UI/DatalistCombobox';
import { defaultRequiresPhysicalHandover } from '../../utils/assetAccess';
import {
  useAssetCategories,
  useAssetTypes,
  useCreateAssetCategory,
  useUpdateAssetCategory,
  useCreateAssetType,
  useUpdateAssetType,
  useVendors,
  useCreateVendor,
  useUpdateVendor,
  useInsuranceProviders,
  useCreateInsuranceProvider,
  useUpdateInsuranceProvider,
  useVehicleLessors,
  useCreateVehicleLessor,
  useUpdateVehicleLessor,
  useWorkshops,
  useCreateWorkshop,
  useUpdateWorkshop,
  useServiceParts,
  useCreateServicePart,
  useUpdateServicePart,
} from '../../hooks/useAssetInventoryData';
import { assetInventoryAPI } from '../../services/api';
import { useTabPersistence } from '../../hooks/usePersistence';
import toast from '../../utils/toast';

// ============================================
// CONSTANTS
// ============================================
const VALID_TABS = ['categories', 'types', 'vendors', 'workshops', 'insurance-providers', 'vehicle-lessors', 'service-parts'];

// ============================================
// CATEGORY ICON COMPONENT (aligned with AssetAdminAssets.js)
// ============================================
const CategoryIcon = ({ code, className = "w-5 h-5" }) => {
  const icons = {
    IT: Laptop,
    ACC: DoorOpen,
    FLT: Car,
    LIC: FileKey,
    OFF: Briefcase,
    FUEL: CreditCard,
    DSL: Fuel,
    TEL: Smartphone,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// CATEGORY CARD COMPONENT
// ============================================
const CategoryCard = ({ category, onEdit, onViewTypes, isExpanded, assetTypes }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-600',
  };
  
  const iconColor = colorClasses[category.color] || colorClasses.gray;
  const categoryTypes = assetTypes?.filter(t => t.category === category.id) || [];
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => onViewTypes(category.id)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-3 min-w-0">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconColor}`}>
              <CategoryIcon code={category.code} className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{category.name}</h3>
              <p className="text-sm text-gray-500 truncate">Code: {category.code}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <StatusBadge status={category.is_active ? 'active' : 'deactivated'} />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
              className="icon-button-admin"
              title="Edit Category"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
        
        {/* Stats */}
        <div className="mt-3 flex items-center space-x-4 text-sm">
          <span className="text-gray-600">
            <span className="font-medium text-gray-900">{category.asset_type_count || 0}</span> Asset Types
          </span>
          <span className="text-gray-600">
            <span className="font-medium text-gray-900">{category.asset_count || 0}</span> Total Assets
          </span>
          {category.custodian_department_name && (
            <span className="text-gray-500">
              Custodian: {category.custodian_department_name}
            </span>
          )}
        </div>
      </div>
      
      {/* Expanded Asset Types List */}
      {isExpanded && categoryTypes.length > 0 && (
        <div className="border-t bg-gray-50 p-4">
          <div className="space-y-2">
            {categoryTypes.map(type => (
              <AssetTypeRow key={type.id} type={type} onEdit={onEdit} />
            ))}
          </div>
        </div>
      )}
      
      {isExpanded && categoryTypes.length === 0 && (
        <div className="border-t bg-gray-50 p-4 text-center text-gray-500 text-sm">
          No asset types defined for this category
        </div>
      )}
    </div>
  );
};

// ============================================
// ASSET TYPE ROW COMPONENT
// ============================================
const HandoverBadge = ({ type }) => (
  type?.requires_physical_handover === false ? (
    <InfoBadge
      color="purple"
      size="xs"
      label="Digital"
      title="No physical handover — condition, photos and storage location do not apply"
    />
  ) : null
);

const AssetTypeRow = ({ type, onEdit }) => {
  const isAccessControl = type.category_code === 'ACC';
  
  return (
    <div className="flex items-center justify-between bg-white rounded-xl p-3 border">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <Package className="w-5 h-5 text-gray-500" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{type.name}</p>
          <p className="text-xs text-gray-500">Code: {type.code}</p>
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        {/* Show appropriate counts based on category type */}
        {isAccessControl ? (
          <>
            <div className="text-center">
              <p className="text-sm font-medium text-green-600">{type.active_count || 0}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">{type.deactivated_count || 0}</p>
              <p className="text-xs text-gray-500">Deactivated</p>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm font-medium text-green-600">{type.available_count || 0}</p>
              <p className="text-xs text-gray-500">Available</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-blue-600">{type.assigned_count || 0}</p>
              <p className="text-xs text-gray-500">Assigned</p>
            </div>
            {(type.deployed_count > 0) && (
              <div className="text-center">
                <p className="text-sm font-medium text-indigo-600">{type.deployed_count}</p>
                <p className="text-xs text-gray-500">Deployed</p>
              </div>
            )}
          </>
        )}
        
        <div className="flex items-center space-x-1">
          {type.required_for_onboarding && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Onboarding
            </span>
          )}
          {type.collect_on_offboarding && (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              Offboarding
            </span>
          )}
          <HandoverBadge type={type} />
        </div>
        
        <StatusBadge status={type.is_active ? 'active' : 'deactivated'} />
        
        <button
          onClick={() => onEdit(type, 'type')}
          className="icon-button-admin"
          title="Edit Asset Type"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// ASSET TYPE TABLE COMPONENT
// ============================================
const AssetTypeTable = ({ types, searchQuery, onEdit }) => {
  const filteredTypes = useMemo(() => {
    if (!searchQuery) return types;
    const query = searchQuery.toLowerCase();
    return types?.filter(t => 
      t.name?.toLowerCase().includes(query) ||
      t.code?.toLowerCase().includes(query) ||
      t.category_name?.toLowerCase().includes(query)
    ) || [];
  }, [types, searchQuery]);
  
  if (!filteredTypes?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Asset Types Found</h3>
        <p className="text-gray-500">
          {searchQuery 
            ? 'No asset types match your search criteria.' 
            : 'Create asset types to define specific assets within categories.'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Mobile card list (below md) */}
      <div className="md:hidden space-y-3 p-4">
        {filteredTypes.map(type => {
          const isAccessControl = type.category_code === 'ACC';
          return (
            <div key={type.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{type.name}</p>
                  <p className="text-xs text-gray-500 truncate">Code: {type.code}</p>
                </div>
                <div className="flex-shrink-0">
                  <StatusBadge status={type.is_active ? 'active' : 'deactivated'} />
                </div>
              </div>
              <div className="space-y-1.5 flex-1">
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-500 flex-shrink-0">Category</span>
                  <span className="text-gray-900 text-right truncate min-w-0">{type.category_name}</span>
                </div>
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-500 flex-shrink-0">Counts</span>
                  <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">
                    {isAccessControl
                      ? `${type.active_count || 0} active / ${type.deactivated_count || 0} deactivated`
                      : `${type.available_count || 0} avail / ${type.assigned_count || 0} assigned${type.deployed_count > 0 ? ` / ${type.deployed_count} deployed` : ''}`}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-500 flex-shrink-0">Lifecycle</span>
                  <span className="text-right min-w-0">
                    <span className="inline-flex flex-wrap justify-end gap-1">
                      {type.required_for_onboarding && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          Onboard
                        </span>
                      )}
                      {type.collect_on_offboarding && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                          Offboard
                        </span>
                      )}
                      <HandoverBadge type={type} />
                      {!type.required_for_onboarding && !type.collect_on_offboarding && type.requires_physical_handover !== false && (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </span>
                  </span>
                </div>
              </div>
              <div className="card-action-footer">
                <button
                  onClick={() => onEdit(type, 'type')}
                  className="icon-button-admin"
                  title="Edit Asset Type"
                  aria-label="Edit Asset Type"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {/* Desktop table (md and up) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Code</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Category</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Counts</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Lifecycle</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredTypes.map(type => {
              const isAccessControl = type.category_code === 'ACC';
              return (
                <tr key={type.id} className="table-row">
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <CategoryIcon code={type.category_code} className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{type.name}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{type.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-full">
                      {type.code}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-700">{type.category_name}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {isAccessControl ? (
                      <div className="flex justify-center space-x-2 text-sm">
                        <span className="text-green-600 font-medium">{type.active_count || 0} active</span>
                        <span className="text-gray-400">/</span>
                        <span className="text-gray-600">{type.deactivated_count || 0} deactivated</span>
                      </div>
                    ) : (
                      <div className="flex justify-center space-x-2 text-sm">
                        <span className="text-green-600 font-medium">{type.available_count || 0} avail</span>
                        <span className="text-gray-400">/</span>
                        <span className="text-blue-600">{type.assigned_count || 0} assigned</span>
                        {(type.deployed_count > 0) && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="text-indigo-600">{type.deployed_count} deployed</span>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center space-x-1">
                      {type.required_for_onboarding && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          Onboard
                        </span>
                      )}
                      {type.collect_on_offboarding && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                          Offboard
                        </span>
                      )}
                      <HandoverBadge type={type} />
                      {!type.required_for_onboarding && !type.collect_on_offboarding && type.requires_physical_handover !== false && (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <StatusBadge status={type.is_active ? 'active' : 'deactivated'} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => onEdit(type, 'type')}
                      className="icon-button-admin"
                      title="Edit Asset Type"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// CATEGORY MODAL COMPONENT
// ============================================
const CategoryModal = ({ isOpen, onClose, category, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    icon: 'package',
    color: 'blue',
    is_active: true,
    display_order: 0,
  });
  
  React.useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        code: category.code || '',
        description: category.description || '',
        icon: category.icon || 'package',
        color: category.color || 'blue',
        is_active: category.is_active ?? true,
        display_order: category.display_order || 0,
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: '',
        icon: 'package',
        color: 'blue',
        is_active: true,
        display_order: 0,
      });
    }
  }, [category, isOpen]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, category?.id);
  };
  
  const colorOptions = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-500' },
    { value: 'green', label: 'Green', class: 'bg-green-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
    { value: 'red', label: 'Red', class: 'bg-red-500' },
    { value: 'gray', label: 'Gray', class: 'bg-gray-500' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? 'Edit Category' : 'Add Category'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="category-form" className="btn btn-admin-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (category ? 'Update' : 'Create')}
          </button>
        </>
      }
    >
      <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-admin w-full"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="input-admin w-full font-mono"
              maxLength={10}
              required
              disabled={!!category}
            />
            {!category && <p className="text-xs text-gray-500 mt-1">Short code (e.g., IT, ACC, FLT)</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-admin w-full"
              rows={3}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex space-x-2">
              {colorOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: opt.value })}
                  className={`w-8 h-8 rounded-full ${opt.class} transition-all focus-visible:outline-none ${
                    formData.color === opt.value ? 'ring-2 ring-offset-2 ring-gray-800' : ''
                  }`}
                  title={opt.label}
                />
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="input-admin w-full"
              min={0}
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active
            </label>
          </div>
      </form>
    </Modal>
  );
};

// ============================================
// ASSET TYPE MODAL COMPONENT
// ============================================
const AssetTypeModal = ({ isOpen, onClose, assetType, categories, onSave, isLoading }) => {
  const handoverTouchedRef = useRef(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    default_warranty_years: 1,
    expected_lifespan_years: 3,
    required_for_onboarding: false,
    collect_on_offboarding: true,
    requires_physical_handover: true,
    requires_serial_number: true,
    requires_purchase_date: true,
    transfer_policy: 'custodian_only',
    is_active: true,
    display_order: 0,
  });

  const suggestedHandover = (categoryId, typeCode = '', typeName = '') => {
    const selectedCategory = categories?.find((category) => String(category.id) === String(categoryId));
    return defaultRequiresPhysicalHandover({
      categoryCode: selectedCategory?.code,
      typeCode,
      typeName,
    });
  };
  
  React.useEffect(() => {
    handoverTouchedRef.current = false;
    if (assetType) {
      setFormData({
        name: assetType.name || '',
        code: assetType.code || '',
        category: assetType.category || '',
        description: assetType.description || '',
        default_warranty_years: assetType.default_warranty_years || 1,
        expected_lifespan_years: assetType.expected_lifespan_years || 3,
        required_for_onboarding: assetType.required_for_onboarding ?? false,
        collect_on_offboarding: assetType.collect_on_offboarding ?? true,
        requires_physical_handover: assetType.requires_physical_handover ?? true,
        requires_serial_number: assetType.requires_serial_number ?? true,
        requires_purchase_date: assetType.requires_purchase_date ?? true,
        transfer_policy: assetType.transfer_policy || 'custodian_only',
        is_active: assetType.is_active ?? true,
        display_order: assetType.display_order || 0,
      });
    } else {
      const initialCategory = categories?.[0];
      setFormData({
        name: '',
        code: '',
        category: initialCategory?.id || '',
        description: '',
        default_warranty_years: 1,
        expected_lifespan_years: 3,
        required_for_onboarding: false,
        collect_on_offboarding: true,
        requires_physical_handover: suggestedHandover(initialCategory?.id),
        requires_serial_number: true,
        requires_purchase_date: true,
        transfer_policy: 'custodian_only',
        is_active: true,
        display_order: 0,
      });
    }
  }, [assetType, isOpen, categories]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, assetType?.id);
  };

  const applySuggestedHandover = (next) => {
    if (assetType || handoverTouchedRef.current) return next;
    return {
      ...next,
      requires_physical_handover: suggestedHandover(next.category, next.code, next.name),
    };
  };

  const handleCategoryChange = (rawCategoryId) => {
    const categoryId = Number(rawCategoryId);
    setFormData((prev) => applySuggestedHandover({ ...prev, category: categoryId }));
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={assetType ? 'Edit Asset Type' : 'Add Asset Type'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="asset-type-form" className="btn btn-admin-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (assetType ? 'Update' : 'Create')}
          </button>
        </>
      }
    >
      <form id="asset-type-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <FormSelect
              theme="admin"
              value={formData.category}
              onChange={handleCategoryChange}
              placeholder="Select Category"
              required
              disabled={!!assetType}
              options={(categories || []).map((cat) => ({
                value: cat.id,
                label: cat.name,
              }))}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => applySuggestedHandover({
                  ...prev,
                  name: e.target.value,
                }))}
                className="input-admin w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData((prev) => applySuggestedHandover({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }))}
                className="input-admin w-full font-mono"
                maxLength={10}
                required
                disabled={!!assetType}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-admin w-full"
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warranty (years)</label>
              <input
                type="number"
                value={formData.default_warranty_years}
                onChange={(e) => setFormData({ ...formData, default_warranty_years: parseInt(e.target.value) || 0 })}
                className="input-admin w-full"
                min={0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lifespan (years)</label>
              <input
                type="number"
                value={formData.expected_lifespan_years}
                onChange={(e) => setFormData({ ...formData, expected_lifespan_years: parseInt(e.target.value) || 0 })}
                className="input-admin w-full"
                min={0}
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Lifecycle Settings</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.required_for_onboarding}
                  onChange={(e) => setFormData({ ...formData, required_for_onboarding: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">Required for Onboarding</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.collect_on_offboarding}
                  onChange={(e) => setFormData({ ...formData, collect_on_offboarding: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">Collect on Offboarding</span>
              </label>
            </div>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Tracking Requirements</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.requires_serial_number}
                  onChange={(e) => setFormData({ ...formData, requires_serial_number: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">Requires Serial Number</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.requires_purchase_date}
                  onChange={(e) => setFormData({ ...formData, requires_purchase_date: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">Requires Purchase Date</span>
              </label>
            </div>
            <label className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_physical_handover}
                onChange={(e) => {
                  handoverTouchedRef.current = true;
                  setFormData({
                    ...formData,
                    requires_physical_handover: e.target.checked,
                  });
                }}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-700">
                  Requires physical handover
                </span>
                <span className="block mt-0.5 text-xs text-gray-500">
                  Collect condition, storage location and photo evidence. Leave this on
                  for laptops, cards, keys and vehicles. Turn it off for software seats
                  or facial-recognition access — those are revoked, not inspected.
                </span>
              </span>
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Policy</label>
            <FormSelect
              theme="admin"
              value={formData.transfer_policy}
              onChange={(value) => setFormData({ ...formData, transfer_policy: value })}
              options={[
                { value: 'custodian_only', label: 'Custodian Only (Return to Inventory First)' },
                { value: 'peer_transfer', label: 'Employee-to-Employee Transfer Allowed' },
              ]}
            />
            <p className="mt-1 text-xs text-gray-500">
              Custodian Only: employees must return the asset to inventory before it can be reassigned.
              Peer Transfer: employees can transfer directly to another employee.
            </p>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="type_is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
            />
            <label htmlFor="type_is_active" className="ml-2 text-sm text-gray-700">
              Active
            </label>
          </div>
      </form>
    </Modal>
  );
};

// ============================================
// ACRA VERIFIED BADGE
// ============================================
const AcraVerifiedBadge = ({ verified }) => {
  if (verified) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <ShieldCheck className="w-3 h-3 mr-1" />
        Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <ShieldAlert className="w-3 h-3 mr-1" />
      Unverified
    </span>
  );
};

const ProviderTypeBadges = ({ isInsurer, isIntermediary }) => {
  if (!isInsurer && !isIntermediary) {
    return <span className="text-gray-400 text-sm">-</span>;
  }
  return (
    <div className="flex flex-wrap justify-center gap-1">
      {isInsurer && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          Insurer
        </span>
      )}
      {isIntermediary && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          Intermediary
        </span>
      )}
    </div>
  );
};

// ============================================
// VENDOR TABLE COMPONENT
// ============================================
const VendorTable = ({ vendors, searchQuery, onEdit, onVerify }) => {
  const filteredVendors = useMemo(() => {
    if (!searchQuery) return vendors;
    const query = searchQuery.toLowerCase();
    return vendors?.filter(v =>
      v.name?.toLowerCase().includes(query) ||
      v.uen?.toLowerCase().includes(query) ||
      v.acra_registered_name?.toLowerCase().includes(query)
    ) || [];
  }, [vendors, searchQuery]);

  if (!filteredVendors?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Vendors Found</h3>
        <p className="text-gray-500">
          {searchQuery
            ? 'No vendors match your search criteria.'
            : 'Create vendors to associate with your assets.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Mobile card list (below md) */}
      <div className="md:hidden space-y-3 p-4">
        {filteredVendors.map(vendor => (
          <div key={vendor.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{vendor.name}</p>
                <p className="text-xs text-gray-500 truncate">{vendor.uen ? `UEN: ${vendor.uen}` : 'No UEN'}</p>
              </div>
              <div className="flex-shrink-0">
                <StatusBadge status={vendor.is_active ? 'active' : 'deactivated'} />
              </div>
            </div>
            <div className="space-y-1.5 flex-1">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">ACRA</span>
                <span className="text-right min-w-0">
                  {vendor.uen ? (
                    <AcraVerifiedBadge verified={vendor.acra_verified} />
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">ACRA Name</span>
                <span className="text-gray-900 text-right truncate min-w-0">
                  {vendor.acra_registered_name && vendor.acra_registered_name !== vendor.name
                    ? vendor.acra_registered_name
                    : '-'}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Assets</span>
                <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{vendor.asset_count || 0}</span>
              </div>
            </div>
            <div className="card-action-footer">
              {vendor.uen && !vendor.acra_verified && (
                <button
                  onClick={() => onVerify(vendor)}
                  className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="Verify UEN"
                  aria-label="Verify UEN"
                >
                  <ShieldCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onEdit(vendor)}
                className="icon-button-admin"
                title="Edit Vendor"
                aria-label="Edit Vendor"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Desktop table (md and up) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">UEN</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">ACRA Name</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Assets</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredVendors.map(vendor => (
              <tr key={vendor.id} className="table-row">
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                      <Store className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="font-medium text-gray-900">{vendor.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  {vendor.uen ? (
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-full">
                        {vendor.uen}
                      </span>
                      <AcraVerifiedBadge verified={vendor.acra_verified} />
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  {vendor.acra_registered_name && vendor.acra_registered_name !== vendor.name ? (
                    <span className="text-sm text-gray-600">{vendor.acra_registered_name}</span>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full">
                    {vendor.asset_count || 0}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge status={vendor.is_active ? 'active' : 'deactivated'} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-1">
                    {vendor.uen && !vendor.acra_verified && (
                      <button
                        onClick={() => onVerify(vendor)}
                        className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Verify UEN"
                        aria-label="Verify UEN"
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(vendor)}
                      className="icon-button-admin"
                      title="Edit Vendor"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// VENDOR MODAL COMPONENT
// ============================================
const VendorModal = ({ isOpen, onClose, vendor, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    uen: '',
    notes: '',
    is_active: true,
  });
  const [uenLookup, setUenLookup] = useState({ status: 'idle', result: null });
  const [verifying, setVerifying] = useState(false);

  React.useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        uen: vendor.uen || '',
        notes: vendor.notes || '',
        is_active: vendor.is_active ?? true,
      });
      if (vendor.acra_verified) {
        setUenLookup({
          status: 'found',
          result: { entity_name: vendor.acra_registered_name },
        });
      } else {
        setUenLookup({ status: 'idle', result: null });
      }
    } else {
      setFormData({ name: '', uen: '', notes: '', is_active: true });
      setUenLookup({ status: 'idle', result: null });
    }
    setVerifying(false);
  }, [vendor, isOpen]);

  const handleVerifyUen = useCallback(async () => {
    const uen = formData.uen.trim().toUpperCase();
    if (!uen) return;
    setVerifying(true);
    setUenLookup({ status: 'loading', result: null });
    try {
      const res = await assetInventoryAPI.verifyVendorUEN(uen);
      const data = res.data;
      if (data.already_exists) {
        setUenLookup({ status: 'exists', result: { existing_vendor_name: data.vendor?.name } });
      } else if (data.verified) {
        setUenLookup({ status: 'found', result: data });
        if (!formData.name) {
          setFormData(prev => ({ ...prev, name: data.entity_name }));
        }
      } else {
        setUenLookup({ status: 'not_found', result: null });
      }
    } catch {
      setUenLookup({ status: 'error', result: null });
    } finally {
      setVerifying(false);
    }
  }, [formData.uen, formData.name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      uen: formData.uen.trim().toUpperCase() || '',
      notes: formData.notes,
      is_active: formData.is_active,
    }, vendor?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={vendor ? 'Edit Vendor' : 'Add Vendor'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="vendor-form" className="btn btn-admin-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (vendor ? 'Update' : 'Create')}
          </button>
        </>
      }
    >
      <form id="vendor-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-admin w-full"
                required
                placeholder="e.g. Challenger Technologies"
              />
            </div>

            {/* UEN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UEN
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.uen}
                  onChange={(e) => {
                    setFormData({ ...formData, uen: e.target.value });
                    setUenLookup({ status: 'idle', result: null });
                  }}
                  className="input-admin flex-1 font-mono"
                  placeholder="e.g. 198400182K"
                  maxLength={20}
                />
                <button
                  type="button"
                  onClick={handleVerifyUen}
                  disabled={!formData.uen.trim() || verifying}
                  className="btn btn-admin-outline whitespace-nowrap"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-1" />
                      Verify
                    </>
                  )}
                </button>
              </div>

              {/* UEN verification feedback */}
              {uenLookup.status === 'found' && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <ShieldCheck className="w-4 h-4 inline mr-1" />
                  ACRA: <strong>{uenLookup.result.entity_name}</strong>
                </div>
              )}
              {uenLookup.status === 'exists' && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  This UEN is already registered to <strong>{uenLookup.result.existing_vendor_name}</strong>
                </div>
              )}
              {uenLookup.status === 'not_found' && (
                <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  UEN not found in ACRA &mdash; you can still save.
                </div>
              )}
              {uenLookup.status === 'error' && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  Verification failed. Try again later.
                </div>
              )}
            </div>

            {/* ACRA Registered Name (read-only) */}
            {vendor?.acra_registered_name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ACRA Registered Name
                </label>
                <input
                  type="text"
                  value={vendor.acra_registered_name}
                  className="input-admin w-full bg-gray-50 text-gray-500"
                  disabled
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-admin w-full"
                rows={3}
                placeholder="Optional notes about this vendor"
              />
            </div>

            {/* Active */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="vendor_is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
              />
              <label htmlFor="vendor_is_active" className="ml-2 text-sm text-gray-700">
                Active
              </label>
            </div>
      </form>
    </Modal>
  );
};

// ============================================
// WORKSHOP TABLE COMPONENT
// ============================================
const WorkshopTable = ({ workshops, searchQuery, onEdit }) => {
  const filtered = useMemo(() => {
    if (!searchQuery) return workshops;
    const query = searchQuery.toLowerCase();
    return workshops?.filter(w =>
      w.name?.toLowerCase().includes(query) ||
      w.contact_person?.toLowerCase().includes(query) ||
      w.phone?.toLowerCase().includes(query) ||
      w.uen?.toLowerCase().includes(query)
    ) || [];
  }, [workshops, searchQuery]);

  if (!filtered?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Workshops Found</h3>
        <p className="text-gray-500">
          {searchQuery
            ? 'No workshops match your search criteria.'
            : 'Add workshops so employees can send vehicles for servicing.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Mobile card list */}
      <div className="md:hidden space-y-3 p-4">
        {filtered.map(w => (
          <div key={w.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                <p className="text-xs text-gray-500 truncate font-mono">
                  {w.uen ? `UEN: ${w.uen}` : 'No UEN'}
                </p>
              </div>
              <StatusBadge status={w.is_active ? 'active' : 'deactivated'} />
            </div>
            <div className="space-y-2 text-sm flex-1">
              {w.address && (
                <div className="flex items-start gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0">{w.address}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="min-w-0 truncate">{w.contact_person || 'No contact person'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {w.phone
                  ? <a href={`tel:${w.phone}`} className="text-blue-600 hover:underline truncate min-w-0">{w.phone}</a>
                  : <span className="text-gray-400">No phone</span>}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {w.email
                  ? <a href={`mailto:${w.email}`} className="text-blue-600 hover:underline truncate min-w-0">{w.email}</a>
                  : <span className="text-gray-400">No email</span>}
              </div>
            </div>
            <div className="card-action-footer">
              <button
                onClick={() => onEdit(w)}
                className="icon-button-admin"
                title="Edit Workshop"
                aria-label="Edit Workshop"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Workshop</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">UEN</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Address</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Contact</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(w => (
              <tr key={w.id} className="table-row align-top">
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <Wrench className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="font-medium text-gray-900">{w.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  {w.uen ? (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-full">
                      {w.uen}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-600 max-w-xs">
                  {w.address ? (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span>{w.address}</span>
                    </div>
                  ) : '-'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-600">
                  <div className="space-y-1">
                    {w.contact_person && (
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900">{w.contact_person}</span>
                      </div>
                    )}
                    {w.phone && (
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${w.phone}`} className="text-blue-600 hover:underline">{w.phone}</a>
                      </div>
                    )}
                    {w.email && (
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${w.email}`} className="text-blue-600 hover:underline truncate">{w.email}</a>
                      </div>
                    )}
                    {!w.contact_person && !w.phone && !w.email && (
                      <span className="text-gray-400">No contact details</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge status={w.is_active ? 'active' : 'deactivated'} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => onEdit(w)}
                      className="icon-button-admin"
                      title="Edit Workshop"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// WORKSHOP MODAL COMPONENT
// ============================================
const WorkshopModal = ({ isOpen, onClose, workshop, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '', uen: '', phone: '', email: '', contact_person: '', address: '', notes: '', is_active: true,
  });
  // Reps / contacts for this workshop, managed inline and saved with the workshop.
  const [contacts, setContacts] = useState([]);

  React.useEffect(() => {
    if (workshop) {
      setFormData({
        name: workshop.name || '',
        uen: workshop.uen || '',
        phone: workshop.phone || '',
        email: workshop.email || '',
        contact_person: workshop.contact_person || '',
        address: workshop.address || '',
        notes: workshop.notes || '',
        is_active: workshop.is_active ?? true,
      });
      setContacts(
        (workshop.contacts || []).map((c) => ({
          id: c.id,
          name: c.name || '',
          phone: c.phone || '',
          is_active: c.is_active ?? true,
          times_seen: c.times_seen ?? 0,
        }))
      );
    } else {
      setFormData({ name: '', uen: '', phone: '', email: '', contact_person: '', address: '', notes: '', is_active: true });
      setContacts([]);
    }
  }, [workshop, isOpen]);

  const addContact = () => setContacts((prev) => [...prev, { name: '', phone: '', is_active: true, times_seen: 0 }]);
  const updateContact = (idx, patch) =>
    setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const removeContact = (idx) => setContacts((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only send contacts with a name; backend de-dupes + deletes omitted rows.
    const cleanContacts = contacts
      .filter((c) => c.name.trim())
      .map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        name: c.name.trim(),
        phone: c.phone.trim(),
        is_active: c.is_active,
      }));
    onSave(
      { ...formData, uen: formData.uen.trim().toUpperCase() || '', contacts: cleanContacts },
      workshop?.id
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={workshop ? 'Edit Workshop' : 'Add Workshop'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">Cancel</button>
          <button
            type="submit"
            form="workshop-form"
            disabled={isLoading || !formData.name.trim()}
            className="btn btn-admin-primary"
          >
            {isLoading ? 'Saving...' : (workshop ? 'Save Changes' : 'Add Workshop')}
          </button>
        </>
      }
    >
      <form id="workshop-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-admin w-full"
                required
                placeholder="e.g. Choan's Motor Work"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="input-admin w-full"
                  placeholder="e.g. Mr Ng"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-admin w-full"
                  placeholder="e.g. 6123 4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-admin w-full"
                  placeholder="workshop@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UEN</label>
                <input
                  type="text"
                  value={formData.uen}
                  onChange={(e) => setFormData({ ...formData, uen: e.target.value })}
                  className="input-admin w-full font-mono"
                  placeholder="optional"
                  maxLength={20}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-admin w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-admin w-full"
              />
            </div>

            {/* Workshop reps / contacts */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reps / Contacts</label>
                  <p className="text-xs text-gray-500">
                    People at this workshop. These appear as a pick-list when sending a vehicle here.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addContact}
                  className="btn btn-admin-outline btn-sm flex items-center gap-1 flex-shrink-0"
                >
                  <UserPlus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {contacts.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-2">No reps yet. Add one so employees can pick them at handover.</p>
              ) : (
                <div className="space-y-2">
                  {contacts.map((c, idx) => (
                    <div key={c.id ?? `new-${idx}`} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={c.name}
                          onChange={(e) => updateContact(idx, { name: e.target.value })}
                          placeholder="Rep name (e.g. Terk)"
                          className="input-admin w-full"
                        />
                      </div>
                      <div className="w-32 flex-shrink-0">
                        <input
                          type="text"
                          value={c.phone}
                          onChange={(e) => updateContact(idx, { phone: e.target.value })}
                          placeholder="Phone"
                          className="input-admin w-full"
                        />
                      </div>
                      {c.times_seen > 0 && (
                        <span
                          className="text-[11px] text-gray-400 flex-shrink-0 w-14 text-center"
                          title={`Used ${c.times_seen} time(s)`}
                        >
                          {c.times_seen}×
                        </span>
                      )}
                      <label className="flex items-center gap-1 flex-shrink-0 cursor-pointer" title="Active">
                        <input
                          type="checkbox"
                          checked={c.is_active}
                          onChange={(e) => updateContact(idx, { is_active: e.target.checked })}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeContact(idx)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                        title="Remove rep"
                        aria-label="Remove rep"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
      </form>
    </Modal>
  );
};

// ============================================
// SERVICE PART TABLE COMPONENT
// ============================================
const SERVICE_PART_GROUP_LABELS = {
  service: 'Service & Jobs',
  fluids: 'Fluids',
  filters: 'Filters',
  engine: 'Engine',
  cooling: 'Cooling & Air-Con',
  brakes: 'Brakes',
  tyres: 'Tyres & Wheels',
  suspension: 'Suspension',
  steering: 'Steering',
  transmission: 'Transmission',
  electrical: 'Electrical',
  body: 'Body',
};

const formatServicePartGroup = (group) =>
  SERVICE_PART_GROUP_LABELS[group] ||
  (group ? group.charAt(0).toUpperCase() + group.slice(1) : 'Uncategorised');

const slugifyServicePartCode = (value) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const ServicePartAliasCount = ({ synonyms }) => {
  const count = synonyms?.length || 0;
  if (!count) {
    return <span className="text-xs text-gray-400">No aliases</span>;
  }
  return (
    <span
      className="text-xs text-gray-500 cursor-help"
      title={synonyms.join(', ')}
    >
      {count} {count === 1 ? 'alias' : 'aliases'}
    </span>
  );
};

const ServicePartTable = ({ parts, searchQuery, onEdit }) => {
  // Filter by name / aliases only — ops searches by what they read, not the
  // internal code. Then group into category sections (preserving the
  // display_order the API already sorts by).
  const groups = useMemo(() => {
    const query = searchQuery?.toLowerCase().trim();
    const filtered = (parts || []).filter((p) => {
      if (!query) return true;
      return (
        p.name?.toLowerCase().includes(query) ||
        (p.synonyms || []).some((s) => s?.toLowerCase().includes(query))
      );
    });

    const order = [];
    const byGroup = new Map();
    filtered.forEach((p) => {
      const key = p.group || '';
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key).push(p);
    });
    return order.map((key) => ({ key, label: formatServicePartGroup(key), items: byGroup.get(key) }));
  }, [parts, searchQuery]);

  const total = groups.reduce((sum, g) => sum + g.items.length, 0);

  if (!total) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <Cog className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Service Parts Found</h3>
        <p className="text-gray-500">
          {searchQuery
            ? 'No service parts match your search criteria.'
            : 'Add common service items employees can log when a vehicle returns.'}
        </p>
      </div>
    );
  }

  const renderRowActions = (p) => (
    <button
      onClick={() => onEdit(p)}
      className="icon-button-admin"
      title="Edit"
      aria-label={`Edit ${p.name}`}
    >
      <Edit2 className="w-4 h-4" />
    </button>
  );

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.key || 'uncategorised'} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 border-b">
            <h3 className="text-sm font-semibold text-gray-700">{group.label}</h3>
            <span className="text-xs text-gray-400">
              {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
            </span>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {group.items.map((p) => (
              <div key={p.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center min-w-0">
                    <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <Cog className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                      <ServicePartAliasCount synonyms={p.synonyms} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={p.is_active ? 'active' : 'deactivated'} />
                    {renderRowActions(p)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <tbody className="divide-y divide-gray-100">
                {group.items.map((p) => (
                  <tr key={p.id} className="table-row">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                          <Cog className="w-5 h-5 text-teal-600" />
                        </div>
                        <span className="font-medium text-gray-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <ServicePartAliasCount synonyms={p.synonyms} />
                    </td>
                    <td className="py-3 px-4 text-center w-32">
                      <StatusBadge status={p.is_active ? 'active' : 'deactivated'} />
                    </td>
                    <td className="py-3 px-4 text-right w-20">
                      {renderRowActions(p)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// SERVICE PART MODAL COMPONENT
// ============================================
const ServicePartModal = ({ isOpen, onClose, part, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    code: '', name: '', group: '', synonyms: '', display_order: 0, is_active: true,
  });

  React.useEffect(() => {
    if (part) {
      setFormData({
        code: part.code || '',
        name: part.name || '',
        group: part.group || '',
        synonyms: (part.synonyms || []).join(', '),
        display_order: part.display_order ?? 0,
        is_active: part.is_active ?? true,
      });
    } else {
      setFormData({ code: '', name: '', group: '', synonyms: '', display_order: 0, is_active: true });
    }
  }, [part, isOpen]);

  // Code is an internal slug: immutable when editing, auto-derived from the
  // name when creating so ops never hand-craft a machine code.
  const derivedCode = part ? formData.code : slugifyServicePartCode(formData.name);

  const handleSubmit = (e) => {
    e.preventDefault();
    const synonyms = formData.synonyms
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onSave({
      code: derivedCode,
      name: formData.name.trim(),
      group: formData.group.trim(),
      synonyms,
      display_order: Number(formData.display_order) || 0,
      is_active: formData.is_active,
    }, part?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={part ? 'Edit Service Part' : 'Add Service Part'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">Cancel</button>
          <button
            type="submit"
            form="service-part-form"
            disabled={isLoading || !formData.name.trim() || !derivedCode}
            className="btn btn-admin-primary"
          >
            {isLoading ? 'Saving...' : (part ? 'Save Changes' : 'Add Service Part')}
          </button>
        </>
      }
    >
      <form id="service-part-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-admin w-full"
                  required
                  placeholder="e.g. Engine Oil"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={derivedCode}
                  className="input-admin w-full font-mono bg-gray-50 text-gray-500"
                  disabled
                  placeholder="auto-generated"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {part ? 'Internal identifier (cannot be changed).' : 'Generated automatically from the name.'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                <DatalistCombobox
                  value={formData.group}
                  onChange={(val) => setFormData({ ...formData, group: val })}
                  suggestions={Object.keys(SERVICE_PART_GROUP_LABELS)}
                  placeholder="e.g. fluids"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                  className="input-admin w-full"
                  min={0}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aliases / synonyms (comma-separated)
              </label>
              <input
                type="text"
                value={formData.synonyms}
                onChange={(e) => setFormData({ ...formData, synonyms: e.target.value })}
                className="input-admin w-full"
                placeholder="e.g. e.oil, eng oil, motor oil"
              />
              <p className="text-xs text-gray-500 mt-1">Used to match OCR'd service-chit line items to this part.</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
      </form>
    </Modal>
  );
};

// ============================================
// INSURANCE PROVIDER TABLE COMPONENT
// ============================================
const InsuranceProviderTable = ({ providers, searchQuery, onEdit, onVerify }) => {
  const filtered = useMemo(() => {
    if (!searchQuery) return providers;
    const query = searchQuery.toLowerCase();
    return providers?.filter(p =>
      p.company_name?.toLowerCase().includes(query) ||
      p.uen?.toLowerCase().includes(query) ||
      p.type_display?.toLowerCase().includes(query)
    ) || [];
  }, [providers, searchQuery]);

  if (!filtered?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Insurance Providers Found</h3>
        <p className="text-gray-500">
          {searchQuery
            ? 'No providers match your search criteria.'
            : 'Add insurance companies and brokers used on fleet vehicles.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="md:hidden space-y-3 p-4">
        {filtered.map(provider => (
          <div key={provider.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{provider.company_name}</p>
                <p className="text-xs text-gray-500 truncate font-mono">UEN: {provider.uen}</p>
              </div>
              <StatusBadge status={provider.is_active ? 'active' : 'deactivated'} />
            </div>
            <div className="space-y-1.5 flex-1">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Type</span>
                <ProviderTypeBadges isInsurer={provider.is_insurer} isIntermediary={provider.is_intermediary} />
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">ACRA</span>
                <AcraVerifiedBadge verified={provider.acra_verified} />
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Vehicles</span>
                <span className="text-gray-900 tabular-nums">{provider.vehicle_count || 0}</span>
              </div>
            </div>
            <div className="card-action-footer">
              {!provider.acra_verified && (
                <button
                  onClick={() => onVerify(provider)}
                  className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="Verify UEN"
                  aria-label="Verify UEN"
                >
                  <ShieldCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onEdit(provider)}
                className="icon-button-admin"
                title="Edit Provider"
                aria-label="Edit Provider"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Company</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">UEN</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Type</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Vehicles</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(provider => (
              <tr key={provider.id} className="table-row">
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <ShieldCheck className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="font-medium text-gray-900">{provider.company_name}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-full">
                      {provider.uen}
                    </span>
                    <AcraVerifiedBadge verified={provider.acra_verified} />
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <ProviderTypeBadges isInsurer={provider.is_insurer} isIntermediary={provider.is_intermediary} />
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full tabular-nums">
                    {provider.vehicle_count || 0}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge status={provider.is_active ? 'active' : 'deactivated'} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-1">
                    {!provider.acra_verified && (
                      <button
                        onClick={() => onVerify(provider)}
                        className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Verify UEN"
                        aria-label="Verify UEN"
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(provider)}
                      className="icon-button-admin"
                      title="Edit Provider"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// INSURANCE PROVIDER MODAL COMPONENT
// ============================================
const InsuranceProviderModal = ({ isOpen, onClose, provider, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    uen: '',
    is_insurer: false,
    is_intermediary: false,
    notes: '',
    is_active: true,
  });
  const [uenLookup, setUenLookup] = useState({ status: 'idle', result: null });
  const [verifying, setVerifying] = useState(false);

  React.useEffect(() => {
    if (provider) {
      setFormData({
        company_name: provider.company_name || '',
        uen: provider.uen || '',
        is_insurer: provider.is_insurer ?? false,
        is_intermediary: provider.is_intermediary ?? false,
        notes: provider.notes || '',
        is_active: provider.is_active ?? true,
      });
      setUenLookup(
        provider.acra_verified
          ? { status: 'found', result: { entity_name: provider.company_name } }
          : { status: 'idle', result: null }
      );
    } else {
      setFormData({
        company_name: '',
        uen: '',
        is_insurer: false,
        is_intermediary: true,
        notes: '',
        is_active: true,
      });
      setUenLookup({ status: 'idle', result: null });
    }
    setVerifying(false);
  }, [provider, isOpen]);

  const handleVerifyUen = useCallback(async () => {
    const uen = formData.uen.trim().toUpperCase();
    if (!uen) return;
    setVerifying(true);
    setUenLookup({ status: 'loading', result: null });
    try {
      const res = await assetInventoryAPI.verifyInsuranceProviderUEN(uen);
      const data = res.data;
      if (data.already_exists) {
        setUenLookup({ status: 'exists', result: { existing_name: data.provider?.company_name } });
      } else if (data.verified) {
        setUenLookup({ status: 'found', result: data });
        if (!formData.company_name) {
          setFormData(prev => ({ ...prev, company_name: data.entity_name }));
        }
      } else {
        setUenLookup({ status: 'not_found', result: null });
      }
    } catch {
      setUenLookup({ status: 'error', result: null });
    } finally {
      setVerifying(false);
    }
  }, [formData.uen, formData.company_name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.is_insurer && !formData.is_intermediary) {
      toast.error('Select at least one provider type (Insurer or Intermediary).');
      return;
    }
    onSave({
      company_name: formData.company_name.trim(),
      uen: formData.uen.trim().toUpperCase(),
      is_insurer: formData.is_insurer,
      is_intermediary: formData.is_intermediary,
      notes: formData.notes,
      is_active: formData.is_active,
    }, provider?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={provider ? 'Edit Insurance Provider' : 'Add Insurance Provider'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="insurance-provider-form" className="btn btn-admin-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (provider ? 'Update' : 'Create')}
          </button>
        </>
      }
    >
      <form id="insurance-provider-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            UEN <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.uen}
              onChange={(e) => {
                setFormData({ ...formData, uen: e.target.value });
                setUenLookup({ status: 'idle', result: null });
              }}
              className="input-admin flex-1 font-mono"
              placeholder="e.g. 202135698W"
              maxLength={20}
              required
              disabled={!!provider}
            />
            {!provider && (
              <button
                type="button"
                onClick={handleVerifyUen}
                disabled={!formData.uen.trim() || verifying}
                className="btn btn-admin-outline whitespace-nowrap"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    Verify
                  </>
                )}
              </button>
            )}
          </div>
          {uenLookup.status === 'found' && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <ShieldCheck className="w-4 h-4 inline mr-1" />
              ACRA: <strong>{uenLookup.result.entity_name}</strong>
            </div>
          )}
          {uenLookup.status === 'exists' && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              This UEN is already registered to <strong>{uenLookup.result.existing_name}</strong>
            </div>
          )}
          {uenLookup.status === 'not_found' && (
            <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              UEN not found in ACRA &mdash; you can still save.
            </div>
          )}
          {uenLookup.status === 'error' && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Verification failed. Try again later.
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="input-admin w-full"
            required
            placeholder="e.g. NTUC Income Insurance Co-operative Limited"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Provider Type <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">A company can be both an insurer and an intermediary.</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_insurer}
                onChange={(e) => setFormData({ ...formData, is_insurer: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
              />
              <span className="text-sm text-gray-700">Insurer</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_intermediary}
                onChange={(e) => setFormData({ ...formData, is_intermediary: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
              />
              <span className="text-sm text-gray-700">Intermediary (broker/agent)</span>
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="input-admin w-full"
            rows={3}
            placeholder="Optional notes about this provider"
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="provider_is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
          />
          <label htmlFor="provider_is_active" className="ml-2 text-sm text-gray-700">
            Active
          </label>
        </div>
      </form>
    </Modal>
  );
};

// ============================================
// VEHICLE LESSOR TABLE COMPONENT
// ============================================
const VehicleLessorTable = ({ lessors, searchQuery, onEdit }) => {
  const filtered = useMemo(() => {
    if (!searchQuery) return lessors;
    const query = searchQuery.toLowerCase();
    return lessors?.filter(l =>
      l.company_name?.toLowerCase().includes(query) ||
      l.uen?.toLowerCase().includes(query)
    ) || [];
  }, [lessors, searchQuery]);

  if (!filtered?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Vehicle Lessors Found</h3>
        <p className="text-gray-500">
          {searchQuery
            ? 'No lessors match your search criteria.'
            : 'Add lease and rental companies used on fleet vehicles.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="md:hidden space-y-3 p-4">
        {filtered.map(lessor => (
          <div key={lessor.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{lessor.company_name}</p>
                <p className="text-xs text-gray-500 truncate font-mono">UEN: {lessor.uen}</p>
              </div>
              <StatusBadge status={lessor.is_active ? 'active' : 'deactivated'} />
            </div>
            <div className="flex items-start justify-between gap-3 text-sm flex-1">
              <span className="text-gray-500 flex-shrink-0">Vehicles</span>
              <span className="text-gray-900 tabular-nums">{lessor.vehicle_count || 0}</span>
            </div>
            <div className="card-action-footer">
              <button
                onClick={() => onEdit(lessor)}
                className="icon-button-admin"
                title="Edit Lessor"
                aria-label="Edit Lessor"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Company</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">UEN</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Vehicles</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(lessor => (
              <tr key={lessor.id} className="table-row">
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                      <Car className="w-5 h-5 text-indigo-600" />
                    </div>
                    <span className="font-medium text-gray-900">{lessor.company_name}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-full">
                    {lessor.uen}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full tabular-nums">
                    {lessor.vehicle_count || 0}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge status={lessor.is_active ? 'active' : 'deactivated'} />
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => onEdit(lessor)}
                    className="icon-button-admin"
                    title="Edit Lessor"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// VEHICLE LESSOR MODAL COMPONENT
// ============================================
const VehicleLessorModal = ({ isOpen, onClose, lessor, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    uen: '',
    notes: '',
    is_active: true,
  });
  const [uenLookup, setUenLookup] = useState({ status: 'idle', result: null });
  const [verifying, setVerifying] = useState(false);

  React.useEffect(() => {
    if (lessor) {
      setFormData({
        company_name: lessor.company_name || '',
        uen: lessor.uen || '',
        notes: lessor.notes || '',
        is_active: lessor.is_active ?? true,
      });
    } else {
      setFormData({ company_name: '', uen: '', notes: '', is_active: true });
    }
    setUenLookup({ status: 'idle', result: null });
    setVerifying(false);
  }, [lessor, isOpen]);

  const handleVerifyUen = useCallback(async () => {
    const uen = formData.uen.trim().toUpperCase();
    if (!uen) return;
    setVerifying(true);
    setUenLookup({ status: 'loading', result: null });
    try {
      const res = await assetInventoryAPI.verifyVehicleLessorUEN(uen);
      const data = res.data;
      if (data.already_exists) {
        setUenLookup({ status: 'exists', result: { existing_name: data.lessor?.company_name } });
      } else if (data.verified) {
        setUenLookup({ status: 'found', result: data });
        if (!formData.company_name) {
          setFormData(prev => ({ ...prev, company_name: data.entity_name }));
        }
      } else {
        setUenLookup({ status: 'not_found', result: null });
      }
    } catch {
      setUenLookup({ status: 'error', result: null });
    } finally {
      setVerifying(false);
    }
  }, [formData.uen, formData.company_name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      company_name: formData.company_name.trim(),
      uen: formData.uen.trim().toUpperCase(),
      notes: formData.notes,
      is_active: formData.is_active,
    }, lessor?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={lessor ? 'Edit Vehicle Lessor' : 'Add Vehicle Lessor'}
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="vehicle-lessor-form" className="btn btn-admin-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (lessor ? 'Update' : 'Create')}
          </button>
        </>
      }
    >
      <form id="vehicle-lessor-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            UEN <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.uen}
              onChange={(e) => {
                setFormData({ ...formData, uen: e.target.value });
                setUenLookup({ status: 'idle', result: null });
              }}
              className="input-admin flex-1 font-mono"
              placeholder="e.g. 199201624D"
              maxLength={20}
              required
              disabled={!!lessor}
            />
            {!lessor && (
              <button
                type="button"
                onClick={handleVerifyUen}
                disabled={!formData.uen.trim() || verifying}
                className="btn btn-admin-outline whitespace-nowrap"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    Verify
                  </>
                )}
              </button>
            )}
          </div>
          {uenLookup.status === 'found' && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <ShieldCheck className="w-4 h-4 inline mr-1" />
              ACRA: <strong>{uenLookup.result.entity_name}</strong>
            </div>
          )}
          {uenLookup.status === 'exists' && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              This UEN is already registered to <strong>{uenLookup.result.existing_name}</strong>
            </div>
          )}
          {uenLookup.status === 'not_found' && (
            <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              UEN not found in ACRA &mdash; you can still save.
            </div>
          )}
          {uenLookup.status === 'error' && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Verification failed. Try again later.
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="input-admin w-full"
            required
            placeholder="e.g. Goldbell Leasing Pte Ltd"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="input-admin w-full"
            rows={3}
            placeholder="Optional notes about this lessor"
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="lessor_is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
          />
          <label htmlFor="lessor_is_active" className="ml-2 text-sm text-gray-700">
            Active
          </label>
        </div>
      </form>
    </Modal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminCategories = () => {
  const queryClient = useQueryClient();

  // Tab persistence
  const [activeTab, setActiveTab] = useTabPersistence('asset-admin-categories', 'categories', VALID_TABS);
  
  // Data
  const { data: categories, isLoading: categoriesLoading } = useAssetCategories();
  const { data: assetTypes, isLoading: typesLoading } = useAssetTypes();
  const { data: vendors, isLoading: vendorsLoading } = useVendors({ active_only: 'false' });
  const { data: workshops, isLoading: workshopsLoading } = useWorkshops({ active_only: 'false' });
  const { data: insuranceProviders, isLoading: insuranceProvidersLoading } = useInsuranceProviders({ active_only: 'false' });
  const { data: vehicleLessors, isLoading: vehicleLessorsLoading } = useVehicleLessors({ active_only: 'false' });
  const { data: serviceParts, isLoading: servicePartsLoading } = useServiceParts({ active_only: 'false' });
  
  // Mutations
  const createCategory = useCreateAssetCategory();
  const updateCategory = useUpdateAssetCategory();
  const createAssetType = useCreateAssetType();
  const updateAssetType = useUpdateAssetType();
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const createInsuranceProvider = useCreateInsuranceProvider();
  const updateInsuranceProvider = useUpdateInsuranceProvider();
  const createVehicleLessor = useCreateVehicleLessor();
  const updateVehicleLessor = useUpdateVehicleLessor();
  const createWorkshop = useCreateWorkshop();
  const updateWorkshop = useUpdateWorkshop();
  const createServicePart = useCreateServicePart();
  const updateServicePart = useUpdateServicePart();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showInsuranceProviderModal, setShowInsuranceProviderModal] = useState(false);
  const [showVehicleLessorModal, setShowVehicleLessorModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [showServicePartModal, setShowServicePartModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingInsuranceProvider, setEditingInsuranceProvider] = useState(null);
  const [editingVehicleLessor, setEditingVehicleLessor] = useState(null);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [editingServicePart, setEditingServicePart] = useState(null);
  
  const isLoading = categoriesLoading || typesLoading || vendorsLoading || workshopsLoading
    || insuranceProvidersLoading || vehicleLessorsLoading || servicePartsLoading;
  
  // Tabs configuration
  const tabs = [
    { id: 'categories', name: 'Categories', icon: FolderOpen },
    { id: 'types', name: 'Asset Types', icon: Layers },
    { id: 'vendors', name: 'Vendors', icon: Store },
    { id: 'workshops', name: 'Workshops', icon: Wrench },
    { id: 'insurance-providers', name: 'Insurance Providers', icon: ShieldCheck },
    { id: 'vehicle-lessors', name: 'Vehicle Lessors', icon: Car },
    { id: 'service-parts', name: 'Service Parts', icon: Cog },
  ];
  
  // Handlers
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };
  
  const handleEditItem = (item, type) => {
    if (type === 'type') {
      setEditingType(item);
      setShowTypeModal(true);
    } else {
      handleEditCategory(item);
    }
  };
  
  const handleSaveCategory = async (data, id) => {
    try {
      if (id) {
        await updateCategory.mutateAsync({ id, data });
      } else {
        await createCategory.mutateAsync(data);
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };
  
  const handleSaveAssetType = async (data, id) => {
    try {
      if (id) {
        await updateAssetType.mutateAsync({ id, data });
      } else {
        await createAssetType.mutateAsync(data);
      }
      setShowTypeModal(false);
      setEditingType(null);
    } catch (error) {
      console.error('Error saving asset type:', error);
    }
  };
  
  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setShowVendorModal(true);
  };

  const handleVerifyVendor = useCallback(async (vendor) => {
    if (!vendor.uen) return;
    try {
      await assetInventoryAPI.verifyVendor(vendor.id);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    } catch (error) {
      console.error('Error verifying vendor UEN:', error);
    }
  }, [queryClient]);

  const handleSaveVendor = async (data, id) => {
    try {
      if (id) {
        await updateVendor.mutateAsync({ id, data });
      } else {
        await createVendor.mutateAsync(data);
      }
      setShowVendorModal(false);
      setEditingVendor(null);
    } catch (error) {
      console.error('Error saving vendor:', error);
    }
  };

  const handleEditInsuranceProvider = (provider) => {
    setEditingInsuranceProvider(provider);
    setShowInsuranceProviderModal(true);
  };

  const handleVerifyInsuranceProvider = useCallback(async (provider) => {
    if (!provider.uen) return;
    try {
      await assetInventoryAPI.verifyInsuranceProvider(provider.id);
      queryClient.invalidateQueries({ queryKey: ['insurance-providers'] });
      toast.success('Insurance provider verified');
    } catch (error) {
      console.error('Error verifying insurance provider UEN:', error);
      toast.error('Verification failed');
    }
  }, [queryClient]);

  const handleSaveInsuranceProvider = async (data, id) => {
    try {
      if (id) {
        await updateInsuranceProvider.mutateAsync({ id, data });
      } else {
        await createInsuranceProvider.mutateAsync(data);
      }
      setShowInsuranceProviderModal(false);
      setEditingInsuranceProvider(null);
    } catch (error) {
      console.error('Error saving insurance provider:', error);
    }
  };

  const handleEditVehicleLessor = (lessor) => {
    setEditingVehicleLessor(lessor);
    setShowVehicleLessorModal(true);
  };

  const handleSaveVehicleLessor = async (data, id) => {
    try {
      if (id) {
        await updateVehicleLessor.mutateAsync({ id, data });
      } else {
        await createVehicleLessor.mutateAsync(data);
      }
      setShowVehicleLessorModal(false);
      setEditingVehicleLessor(null);
    } catch (error) {
      console.error('Error saving vehicle lessor:', error);
    }
  };

  const handleEditWorkshop = (workshop) => {
    setEditingWorkshop(workshop);
    setShowWorkshopModal(true);
  };

  const handleSaveWorkshop = async (data, id) => {
    try {
      if (id) {
        await updateWorkshop.mutateAsync({ id, data });
      } else {
        await createWorkshop.mutateAsync(data);
      }
      setShowWorkshopModal(false);
      setEditingWorkshop(null);
    } catch (error) {
      console.error('Error saving workshop:', error);
    }
  };

  const handleEditServicePart = (part) => {
    setEditingServicePart(part);
    setShowServicePartModal(true);
  };

  const handleSaveServicePart = async (data, id) => {
    try {
      if (id) {
        await updateServicePart.mutateAsync({ id, data });
      } else {
        await createServicePart.mutateAsync(data);
      }
      setShowServicePartModal(false);
      setEditingServicePart(null);
    } catch (error) {
      console.error('Error saving service part:', error);
    }
  };

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    const query = searchQuery.toLowerCase();
    return categories?.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.code?.toLowerCase().includes(query)
    ) || [];
  }, [categories, searchQuery]);
  
  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-7 bg-gray-200 rounded w-48"></div>
          <div className="h-10 bg-gray-200 rounded-full w-32"></div>
        </div>
        
        {/* Categories Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white rounded-xl border p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Configuration</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage categories, vendors, workshops and fleet master data</p>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
          {activeTab === 'vendors' ? (
            <button
              onClick={() => {
                setEditingVendor(null);
                setShowVendorModal(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </button>
          ) : activeTab === 'workshops' ? (
            <button
              onClick={() => {
                setEditingWorkshop(null);
                setShowWorkshopModal(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Workshop
            </button>
          ) : activeTab === 'insurance-providers' ? (
            <button
              onClick={() => {
                setEditingInsuranceProvider(null);
                setShowInsuranceProviderModal(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </button>
          ) : activeTab === 'vehicle-lessors' ? (
            <button
              onClick={() => {
                setEditingVehicleLessor(null);
                setShowVehicleLessorModal(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lessor
            </button>
          ) : activeTab === 'service-parts' ? (
            <button
              onClick={() => {
                setEditingServicePart(null);
                setShowServicePartModal(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service Part
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setEditingType(null);
                  setShowTypeModal(true);
                }}
                className="btn btn-admin-outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Asset Type
              </button>
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setShowCategoryModal(true);
                }}
                className="btn btn-admin-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Summary Stats - Admin Theme Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{categories?.length || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Categories</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{assetTypes?.length || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Asset Types</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {categories?.filter(c => c.is_active).length || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Active Categories</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {categories?.reduce((sum, c) => sum + (c.asset_count || 0), 0) || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total Assets</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Store className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {vendors?.filter(v => v.is_active).length || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Vendors</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs - Admin Theme Pill Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>
      
      {/* Search - Admin Theme */}
      <div className="flex items-center justify-between gap-3">
        <DebouncedSearchInput
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder={
            activeTab === 'categories' ? 'Search categories...'
            : activeTab === 'types' ? 'Search asset types...'
            : activeTab === 'vendors' ? 'Search vendors by name, UEN...'
            : activeTab === 'workshops' ? 'Search workshops by name, contact...'
            : activeTab === 'insurance-providers' ? 'Search providers by name, UEN...'
            : activeTab === 'vehicle-lessors' ? 'Search lessors by name, UEN...'
            : 'Search service parts by name, code...'
          }
          className="max-w-xl"
        />
        <div className="flex items-center gap-2">
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={0}
            onClick={() => setShowFilters(v => !v)}
          />
        </div>
      </div>
      
      {/* Content */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          {filteredCategories?.length > 0 ? (
            filteredCategories.map(category => (
              <CategoryCard
                key={category.id}
                category={category}
                assetTypes={assetTypes}
                isExpanded={expandedCategory === category.id}
                onEdit={handleEditCategory}
                onViewTypes={toggleCategory}
              />
            ))
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
              <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Found</h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? 'No categories match your search criteria.' 
                  : 'Create categories to organize your assets.'}
              </p>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'types' && (
        <AssetTypeTable
          types={assetTypes}
          searchQuery={searchQuery}
          onEdit={handleEditItem}
        />
      )}

      {activeTab === 'vendors' && (
        <VendorTable
          vendors={vendors}
          searchQuery={searchQuery}
          onEdit={handleEditVendor}
          onVerify={handleVerifyVendor}
        />
      )}

      {activeTab === 'workshops' && (
        <WorkshopTable
          workshops={workshops}
          searchQuery={searchQuery}
          onEdit={handleEditWorkshop}
        />
      )}

      {activeTab === 'insurance-providers' && (
        <InsuranceProviderTable
          providers={insuranceProviders}
          searchQuery={searchQuery}
          onEdit={handleEditInsuranceProvider}
          onVerify={handleVerifyInsuranceProvider}
        />
      )}

      {activeTab === 'vehicle-lessors' && (
        <VehicleLessorTable
          lessors={vehicleLessors}
          searchQuery={searchQuery}
          onEdit={handleEditVehicleLessor}
        />
      )}

      {activeTab === 'service-parts' && (
        <ServicePartTable
          parts={serviceParts}
          searchQuery={searchQuery}
          onEdit={handleEditServicePart}
        />
      )}
      
      {/* Modals */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSaveCategory}
        isLoading={createCategory.isPending || updateCategory.isPending}
      />
      
      <AssetTypeModal
        isOpen={showTypeModal}
        onClose={() => {
          setShowTypeModal(false);
          setEditingType(null);
        }}
        assetType={editingType}
        categories={categories}
        onSave={handleSaveAssetType}
        isLoading={createAssetType.isPending || updateAssetType.isPending}
      />

      <VendorModal
        isOpen={showVendorModal}
        onClose={() => {
          setShowVendorModal(false);
          setEditingVendor(null);
        }}
        vendor={editingVendor}
        onSave={handleSaveVendor}
        isLoading={createVendor.isPending || updateVendor.isPending}
      />

      <WorkshopModal
        isOpen={showWorkshopModal}
        onClose={() => {
          setShowWorkshopModal(false);
          setEditingWorkshop(null);
        }}
        workshop={editingWorkshop}
        onSave={handleSaveWorkshop}
        isLoading={createWorkshop.isPending || updateWorkshop.isPending}
      />

      <InsuranceProviderModal
        isOpen={showInsuranceProviderModal}
        onClose={() => {
          setShowInsuranceProviderModal(false);
          setEditingInsuranceProvider(null);
        }}
        provider={editingInsuranceProvider}
        onSave={handleSaveInsuranceProvider}
        isLoading={createInsuranceProvider.isPending || updateInsuranceProvider.isPending}
      />

      <VehicleLessorModal
        isOpen={showVehicleLessorModal}
        onClose={() => {
          setShowVehicleLessorModal(false);
          setEditingVehicleLessor(null);
        }}
        lessor={editingVehicleLessor}
        onSave={handleSaveVehicleLessor}
        isLoading={createVehicleLessor.isPending || updateVehicleLessor.isPending}
      />

      <ServicePartModal
        isOpen={showServicePartModal}
        onClose={() => {
          setShowServicePartModal(false);
          setEditingServicePart(null);
        }}
        part={editingServicePart}
        onSave={handleSaveServicePart}
        isLoading={createServicePart.isPending || updateServicePart.isPending}
      />
    </div>
  );
};

export default AssetAdminCategories;
